/**
 * Auth Slice
 * Manages authentication state with cookie persistence
 */

import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type { SteContext } from "../../api";
import {
  setAuthCookie,
  getAuthCookie,
  clearAuthCookie,
} from "../../utils/cookies";
import { Analytics } from "../../services/analytics";

interface AuthState {
  token: string | null;
  steContext: SteContext | null;
  isAuthenticated: boolean;
}

// Try to load initial state from cookie
function getInitialState(): AuthState {
  const cookieData = getAuthCookie();
  if (cookieData) {
    // Identify user from persisted session
    Analytics.identify(cookieData.steContext.id, {
      name: cookieData.steContext.name,
      id: cookieData.steContext.id,
    });
    return {
      token: cookieData.token,
      steContext: cookieData.steContext,
      isAuthenticated: true,
    };
  }
  return {
    token: null,
    steContext: null,
    isAuthenticated: false,
  };
}

const authSlice = createSlice({
  name: "auth",
  initialState: getInitialState(),
  reducers: {
    setCredentials: (
      state,
      action: PayloadAction<{ token: string; steContext: SteContext }>,
    ) => {
      state.token = action.payload.token;
      state.steContext = action.payload.steContext;
      state.isAuthenticated = true;
      // Persist to cookie
      setAuthCookie({
        token: action.payload.token,
        steContext: action.payload.steContext,
      });
      // Identify user for analytics
      Analytics.identify(action.payload.steContext.id, {
        name: action.payload.steContext.name,
        id: action.payload.steContext.id,
      });
    },
    logout: (state) => {
      state.token = null;
      state.steContext = null;
      state.isAuthenticated = false;
      // Clear cookie
      clearAuthCookie();
      // Reset analytics
      Analytics.reset();
    },
  },
});

export const { setCredentials, logout } = authSlice.actions;
export default authSlice.reducer;

// Selectors
export const selectCurrentToken = (state: { auth: AuthState }) =>
  state.auth.token;
export const selectSteContext = (state: { auth: AuthState }) =>
  state.auth.steContext;
export const selectIsAuthenticated = (state: { auth: AuthState }) =>
  state.auth.isAuthenticated;
